import _ from 'lodash'
import moment from 'moment'

import CONSTANTS from 'core/data-model/constants/values'
import { getAppConfig } from 'deploy-env/app-config'
import { getToggles } from 'quick-quote/feature-toggles'

import { overridingFundInfo } from './overriding'

export const filterByUserType = (visibleFunds, userAgentType) =>
  _(visibleFunds)
    .filter({ allowUserType: [userAgentType] })
    .value()

//TODO [B] create function for checking wealth hub users
export const isWealthHub = (agentType) => !!agentType && agentType === CONSTANTS.WEALTH_HUB

export const additionalFunds = ['KT-GOI-A', 'KT-MFB', 'KT-MAB', 'KT-MFE']
export const additional2020Q2Funds = ['HI-DIV PLUS', 'KT-EMEQ-A', 'TMBWDEQ']
export const additional2021Q1Funds = ['KT-CHINA-A']
export const removedFunds2021Q2 = ['KT-MFB', 'KT-MFB-A']
export const additional2022Q1Funds = ['KT-EPIC-A']
export const additional2023Q2Funds = ['KT-JAPANSM-A', 'UGIS-N']
export const additional2024Q1Lot1Funds = ['KT-GAEQ-A']
export const additional2024Q1Lot2Funds = ['KTWC-DEFENSIVE-A', 'KTWC-GROWTH-A', 'KTWC-MODERATE-A']

export const containOverRiskFund = (funds) =>
  funds.some((fund) => {
    return fund.isOverRisk
  })

export const containForeignFund = (funds) =>
  funds.some((fund) => {
    return fund.isForeign
  })

export const isAcceptForeignInvestment = (allowForeignInvestment, isSelectedYesOnExchangeRateQuestion) => {
  return isSelectedYesOnExchangeRateQuestion ? isSelectedYesOnExchangeRateQuestion : allowForeignInvestment === true
}

export const fundsContainErrors = (funds) => {
  return funds.some((fund) => fund.errors.length > 0)
}

/**
 * @param investmentRepository {ReturnType<import('core/data-model/investment/repository').createInvestmentRepository>}
 */
export function createInvestmentFundService(investmentRepository) {
  const _getSellableFunds = async (userAgentType, displayProductQuery, listRpulFundsFn, getVisibleFundsFn) => {
    const allFunds = await listRpulFundsFn()
    const visibleFunds = await getVisibleFundsFn(displayProductQuery, allFunds, userAgentType)
    return filterByUserType(visibleFunds, userAgentType)
  }

  const getSellableFunds = async (userAgentType, displayProductQuery) => {
    return _getSellableFunds(
      userAgentType,
      displayProductQuery,
      investmentRepository.listInvestmentFunds,
      getVisibleFunds
    )
  }

  const isActiveForToday = (activeFundConfig) => {
    const DATE_FORMAT = 'DD/MM/YYYY'

    const { startDate, endDate } = activeFundConfig || {}
    const today = moment().startOf('day')

    const hasStart = !!startDate
    const hasEnd = !!endDate

    if (!hasStart && !hasEnd) return true

    const start = hasStart ? moment(startDate, DATE_FORMAT, true).startOf('day') : null
    const end = hasEnd ? moment(endDate, DATE_FORMAT, true).startOf('day') : null

    if (hasStart && (!start || !start.isValid())) return true
    if (hasEnd && (!end || !end.isValid())) return true

    if (start && end) {
      return today.isBetween(start, end, undefined, '[]')
    }
    if (start) {
      return today.isSameOrAfter(start, 'day')
    }
    if (end) {
      return today.isSameOrBefore(end, 'day')
    }

    return true
  }

  const isFundActiveForUserToday = (fund, userAgentType) => {
    const activeFundConfig = Array.isArray(fund.activeFund) ? fund.activeFund : null
    if (!activeFundConfig || activeFundConfig.length === 0) return true

    const activeFundUserType = activeFundConfig.filter(
      (data) => Array.isArray(data.userType) && data.userType.includes(userAgentType)
    )

    if (activeFundUserType.length === 0) return true

    return activeFundUserType.some(isActiveForToday)
  }

  const _getVisibleFunds = (displayProductQuery, allFunds, _getInvestmentProductConfiguration, userAgentType) => {
    return _getInvestmentProductConfiguration(displayProductQuery)
      .then((investmentProductCfg) => {
        const investmentConfigVisibleFunds = investmentProductCfg.visibleFunds
        const isNot2023Q2Funds = (code) => !_.includes(additional2023Q2Funds, code)
        const isNot2024Q1Lot1Funds = (code) => !_.includes(additional2024Q1Lot1Funds, code)
        const isNot2024Q1Lot2Funds = (code) => !_.includes(additional2024Q1Lot2Funds, code)

        const isNotInRemoveList = (code) => {
          return !_.includes(removedFunds2021Q2, code)
        }
        const visibleFunds = allFunds
          .filter((f) => investmentConfigVisibleFunds.includes(f.code))
          .filter((f) => getToggles().SHOW_ADDITIONAL_2023Q2_FUNDS || isNot2023Q2Funds(f.code))
          .filter((f) => getToggles().SHOW_ADDITIONAL_2024Q1_LOT1_FUNDS || isNot2024Q1Lot1Funds(f.code))
          .filter((f) => getToggles().SHOW_ADDITIONAL_2024Q1_LOT2_FUNDS || isNot2024Q1Lot2Funds(f.code))
          .filter((f) => isNotInRemoveList(f.code))
          .filter((f) => isFundActiveForUserToday(f, userAgentType))
          .filter((f) => _.includes(getAppConfig().FUNDS_AVAILABLE, f.code))

        const overrideFunds = allFunds.filter((f) => _.includes(getAppConfig().FUNDS_OVERRIDE, f.code))
        return overrideFunds.length ? overridingFundInfo(visibleFunds, overrideFunds) : visibleFunds
      })
      .catch((e) => {
        console.error(`error when getting visible funds from repository. e=${e}`)
        return []
      })
  }

  const getVisibleFunds = (displayProductQuery, allFunds, userAgentType) => {
    return _getVisibleFunds(
      displayProductQuery,
      allFunds,
      investmentRepository.getInvestmentProductConfiguration,
      userAgentType
    )
  }

  return {
    _getSellableFunds,
    getSellableFunds,
    _getVisibleFunds,
    getVisibleFunds,
  }
}
